/**
 * Billing default scope for a payment method (matches API `defaultScope`).
 *
 * Billing runs through two channels — Stripe (membership) and Invoiced (order) — and the
 * Stripe channel itself has two levels: the customer-level default (account-root) and
 * per-subscription defaults. The API collapses all of that into this single-valued enum by
 * precedence, so the customer-level default reports `RootMembership` (never `Membership`),
 * and `Membership` means "a subscription-level default that is NOT the customer default"
 * — one place's card in a multi-place account.
 *
 * See `platform/docs/payment-method-defaults.md` for the full precedence table.
 */
export enum PaymentDefaultScope {
  /** Default on both channels: the Stripe customer-level default and the Invoiced default. */
  Account = "account",
  /** The Stripe customer-level default (account-root), with no Invoiced default. */
  RootMembership = "root_membership",
  /** A Stripe subscription-level default only — scoped to a single place. */
  Membership = "membership",
  /** The Invoiced customer's default payment source only. */
  Order = "order",
  /** Default for both Invoiced and a Stripe subscription, without the customer-level default. */
  OrderMembership = "order_membership",
}

/**
 * The scopes a caller can *request*, one per set-default endpoint.
 *
 * `RootMembership` and `OrderMembership` are read-only outcomes of the API's precedence
 * collapse — there is no endpoint that sets them, so they can never be requested.
 */
export type PaymentDefaultActionScope =
  | PaymentDefaultScope.Account
  | PaymentDefaultScope.Order
  | PaymentDefaultScope.Membership;
